import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { PermissionStatus, Position } from '@capacitor/geolocation';
import { ThemeProvider } from '@mui/material';

import { driverTheme, passengerThemeV2 } from '@mopla/mui';

import { MplModal } from '../MplModal/MplModal';

import { PermissionModalButtons } from './components/PermissionModalButtonts';
import { PermissionModalContent } from './components/PermissionModalContent';
import { usePermissionsModal } from './usePermissionsModal';

export interface PermissionsModalProps {
	isOpen: boolean;
	permissionRequestTitle: string;
	permissionRequestDescription: string;
	permissionRequestSubDescription?: string;
	permissionRequestButtonAcceptText: string;
	permissionRequestButtonSkipText?: string;
	permissionDeniedTitle?: string;
	permissionDeniedDescription: string;
	permissionDeniedSubDescription?: string;
	permissionDeniedButtonAcceptText: string;
	permissionDeniedButtonSkipText?: string;
	animationElement: JSX.Element;
	askPermissionsCallback: () => Promise<any>;
	checkPermissionsCallback: () => Promise<any>;
	onPermissionsGranted:
		| VoidFunction
		| (() => Promise<void>)
		| ((arg: PermissionStatus | Position) => Promise<void>);
	onCancel: () => void;
	deferred?: boolean;
	isDriverApp?: boolean;
}

export interface IPermissionsModalRef {
	reprocess(): Promise<void>;
}

export const PermissionsModal = forwardRef<
	IPermissionsModalRef,
	PermissionsModalProps
>(
	(
		{
			isOpen,
			permissionRequestTitle,
			permissionRequestDescription,
			permissionRequestSubDescription,
			permissionRequestButtonAcceptText,
			permissionRequestButtonSkipText,
			permissionDeniedDescription,
			permissionDeniedSubDescription,
			permissionDeniedButtonAcceptText,
			permissionDeniedButtonSkipText,
			animationElement,
			askPermissionsCallback,
			checkPermissionsCallback,
			onPermissionsGranted,
			permissionDeniedTitle,
			onCancel,
			deferred = false,
			isDriverApp = false,
		},
		ref
	) => {
		const theme = isDriverApp ? driverTheme : passengerThemeV2;
		const {
			isRequestAccessModal,
			goToSettings,
			processPermission,
			askPermissions,
			cancelHandler,
			isAccessErrorModal,
		} = usePermissionsModal({
			isOpen,
			checkPermissionsCallback,
			onCancel,
			onPermissionsGranted,
			askPermissionsCallback,
		});

		useEffect(() => {
			if (!deferred) {
				processPermission();
			}
		}, [processPermission, deferred]);

		useImperativeHandle(ref, () => ({ reprocess: processPermission }), [
			processPermission,
		]);

		return (
			<ThemeProvider theme={theme}>
				<MplModal
					open={isRequestAccessModal}
					testId="PermissionsModal-isAccessRequest"
					icon={animationElement}
					title={permissionRequestTitle}
					content={
						<PermissionModalContent
							description={permissionRequestDescription}
							subDescription={permissionRequestSubDescription}
						/>
					}
					buttons={
						<PermissionModalButtons
							acceptText={permissionRequestButtonAcceptText}
							skipText={permissionRequestButtonSkipText}
							onAccept={askPermissions}
							onSkip={cancelHandler}
						/>
					}
				/>
				<MplModal
					open={isAccessErrorModal}
					testId="PermissionsModal-isAccessErrorModal"
					icon={animationElement}
					title={permissionDeniedTitle}
					content={
						<PermissionModalContent
							description={permissionDeniedDescription}
							subDescription={permissionDeniedSubDescription}
						/>
					}
					buttons={
						<PermissionModalButtons
							acceptText={permissionDeniedButtonAcceptText}
							skipText={permissionDeniedButtonSkipText}
							onAccept={goToSettings}
							onSkip={cancelHandler}
						/>
					}
				/>
			</ThemeProvider>
		);
	}
);
PermissionsModal.displayName = 'PermissionsModal';

export default PermissionsModal;
